/**
 * CalendarPage — Executive Calendar (admin only).
 *
 * Read-only view over payments / tasks / milestones / deliveries via
 * GET /api/calendar/events. Default range = -30..+60 days, view = month
 * (desktop) or agenda (mobile). Click an event → detail panel with a CTA
 * to open the related module.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminShell from "@/pages/admin/AdminShell";
import { Section } from "@/pages/Shell";
import { api, formatError } from "@/lib/api";
import MonthGrid from "@/components/calendar/MonthGrid";
import AgendaList from "@/components/calendar/AgendaList";
import EventBadge, { EVENT_TYPES, EVENT_TYPE_LABEL } from "@/components/calendar/EventBadge";
import {
  CaretLeft, CaretRight, CalendarBlank, ListBullets, X, ArrowSquareOut, CircleNotch,
} from "@phosphor-icons/react";

const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d)   { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

function defaultView() {
  if (typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches) {
    return "agenda";
  }
  return "month";
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);
  const todayISO = useMemo(() => isoDate(today), [today]);

  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [view, setView] = useState(defaultView);
  const [activeTypes, setActiveTypes] = useState(new Set(EVENT_TYPES));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [detail, setDetail] = useState(null); // event being viewed in panel

  // Range = whole month for "month" view, ±30 days for "agenda"
  const range = useMemo(() => {
    if (view === "month") {
      return {
        start: isoDate(startOfMonth(cursor)),
        end: isoDate(endOfMonth(cursor)),
      };
    }
    const s = new Date(cursor); s.setDate(s.getDate() - 14);
    const e = new Date(cursor); e.setDate(e.getDate() + 45);
    return { start: isoDate(s), end: isoDate(e) };
  }, [cursor, view]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr("");
    api.get(`/calendar/events?start=${range.start}&end=${range.end}`)
      .then(r => { if (!cancelled) setEvents(r.data?.items || []); })
      .catch(e => { if (!cancelled) setErr(formatError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range.start, range.end]);

  const filteredEvents = useMemo(
    () => events.filter(e => activeTypes.has(e.type)),
    [events, activeTypes],
  );

  const dayEvents = selectedDate
    ? filteredEvents.filter(e => e.date === selectedDate)
    : [];

  const toggleType = (t) => {
    setActiveTypes(prev => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      // never let it go to empty: re-toggling brings it back
      if (next.size === 0) next.add(t);
      return next;
    });
  };

  const goToToday = () => {
    setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const navMonth = (delta) => {
    setCursor(c => new Date(c.getFullYear(), c.getMonth() + delta, 1));
  };

  const handleOpenInModule = (ev) => {
    if (ev.link) navigate(ev.link);
  };

  return (
    <AdminShell>
      <Section
        title="Calendario ejecutivo"
        subtitle="Vista cronológica unificada de pagos, tareas, hitos y entregas."
      >
        {/* Controls bar */}
        <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="calendar-controls">
          {/* View toggle */}
          <div className="inline-flex bg-stone-100 rounded-full p-1" data-testid="view-toggle">
            <button
              onClick={() => setView("month")}
              data-testid="view-month"
              className={`px-3 py-1 rounded-full text-sm flex items-center gap-1.5 transition-colors ${
                view === "month" ? "bg-white shadow-sm text-stone-900" : "text-stone-500"
              }`}
            >
              <CalendarBlank size={14} weight="bold" /> Mes
            </button>
            <button
              onClick={() => setView("agenda")}
              data-testid="view-agenda"
              className={`px-3 py-1 rounded-full text-sm flex items-center gap-1.5 transition-colors ${
                view === "agenda" ? "bg-white shadow-sm text-stone-900" : "text-stone-500"
              }`}
            >
              <ListBullets size={14} weight="bold" /> Agenda
            </button>
          </div>

          {/* Month nav */}
          <div className="inline-flex items-center gap-1.5">
            <button
              onClick={() => navMonth(-1)}
              data-testid="month-prev"
              className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center"
              aria-label="Mes anterior"
            >
              <CaretLeft size={14} weight="bold" />
            </button>
            <div className="font-serif-dv text-lg min-w-[170px] text-center" data-testid="month-label">
              {MONTHS_ES[cursor.getMonth()]} {cursor.getFullYear()}
            </div>
            <button
              onClick={() => navMonth(1)}
              data-testid="month-next"
              className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center"
              aria-label="Mes siguiente"
            >
              <CaretRight size={14} weight="bold" />
            </button>
          </div>

          <button
            onClick={goToToday}
            data-testid="goto-today"
            className="text-xs uppercase tracking-widest text-stone-500 hover:text-stone-900 px-2 py-1"
          >
            Hoy
          </button>

          {/* Type filters */}
          <div className="ml-auto flex flex-wrap gap-1.5" data-testid="type-filters">
            {EVENT_TYPES.map(t => {
              const active = activeTypes.has(t);
              return (
                <button
                  key={t}
                  onClick={() => toggleType(t)}
                  data-testid={`filter-${t}`}
                  className={`text-xs px-2.5 py-1 rounded-full transition ${
                    active
                      ? "bg-stone-900 text-white"
                      : "bg-stone-100 text-stone-500 hover:bg-stone-200"
                  }`}
                >
                  {EVENT_TYPE_LABEL[t]}
                </button>
              );
            })}
          </div>

          <span className="text-xs text-stone-400 w-full sm:w-auto" data-testid="event-count">
            {loading ? "Cargando…" : `${filteredEvents.length} eventos`}
          </span>
        </div>

        {err && (
          <div className="dv-card border-red-200 bg-red-50 text-red-800 text-sm mb-4" data-testid="calendar-error">
            {err}
          </div>
        )}

        {loading ? (
          <div className="dv-card text-center py-16 text-stone-500" data-testid="calendar-loading">
            <CircleNotch size={28} className="animate-spin mx-auto mb-2 text-stone-400" />
            Cargando calendario…
          </div>
        ) : view === "month" ? (
          <MonthGrid
            year={cursor.getFullYear()}
            month={cursor.getMonth()}
            events={filteredEvents}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            todayISO={todayISO}
          />
        ) : (
          <AgendaList
            events={filteredEvents}
            todayISO={todayISO}
            onEventClick={setDetail}
          />
        )}

        {/* Empty state for month view */}
        {!loading && view === "month" && filteredEvents.length === 0 && (
          <div className="text-center text-stone-400 text-sm mt-6" data-testid="month-empty">
            Sin eventos en este mes con los filtros actuales.
          </div>
        )}
      </Section>

      {/* Day-list panel for Month view */}
      {selectedDate && (
        <SidePanel
          title={new Date(selectedDate + "T00:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
          onClose={() => setSelectedDate(null)}
        >
          {dayEvents.length === 0 ? (
            <div className="text-stone-500 text-sm">Sin eventos este día.</div>
          ) : (
            <ul className="space-y-2">
              {dayEvents.map(ev => (
                <li key={ev.event_id}>
                  <button
                    onClick={() => setDetail(ev)}
                    data-testid={`day-event-${ev.event_id}`}
                    className="w-full text-left flex items-center gap-3 border border-stone-200 rounded px-3 py-2 hover:border-stone-400"
                  >
                    <EventBadge type={ev.type} severity={ev.severity} size="xs" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{ev.title}</div>
                      <div className="text-xs text-stone-500 truncate">{ev.subtitle}</div>
                    </div>
                    {ev.amount_eur && (
                      <div className="text-sm whitespace-nowrap">€{Number(ev.amount_eur).toLocaleString("es-ES")}</div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </SidePanel>
      )}

      {/* Event detail panel */}
      {detail && (
        <SidePanel
          title={null}
          onClose={() => setDetail(null)}
          testId="event-detail"
        >
          <EventDetail event={detail} todayISO={todayISO} onOpen={handleOpenInModule} />
        </SidePanel>
      )}
    </AdminShell>
  );
}

function SidePanel({ title, onClose, children, testId = "side-panel" }) {
  return (
    <div className="fixed inset-0 z-50" data-testid={testId}>
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="absolute top-0 right-0 h-full w-full sm:w-[420px] bg-white shadow-xl p-6 overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div className="font-serif-dv text-xl capitalize">{title}</div>
          <button
            onClick={onClose}
            data-testid={`${testId}-close`}
            className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center"
            aria-label="Cerrar"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
        {children}
      </aside>
    </div>
  );
}

function EventDetail({ event, todayISO, onOpen }) {
  const days = (() => {
    const a = new Date(event.date + "T00:00:00").getTime();
    const b = new Date(todayISO + "T00:00:00").getTime();
    return Math.round((a - b) / 86400000);
  })();
  const relLabel = days === 0 ? "hoy" : days > 0 ? `en ${days} días` : `hace ${Math.abs(days)} días`;
  return (
    <div className="space-y-5">
      <div>
        <EventBadge type={event.type} severity={event.severity} size="md" />
      </div>
      <div>
        <div className="font-serif-dv text-2xl">{event.title}</div>
        {event.subtitle && (
          <div className="text-stone-500 mt-1">{event.subtitle}</div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <Field label="Fecha" value={`${new Date(event.date + "T00:00:00").toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })} · ${relLabel}`} />
        {event.amount_eur && (
          <Field label="Importe" value={`€${Number(event.amount_eur).toLocaleString("es-ES")}`} />
        )}
        {event.status && <Field label="Estado" value={event.status} />}
        <Field label="Severidad" value={event.severity} />
      </div>

      {event.link && (
        <button
          onClick={() => onOpen(event)}
          data-testid="event-detail-open"
          className="w-full mt-4 dv-btn text-white inline-flex items-center justify-center gap-2"
          style={{ background: "#9C5236" }}
        >
          Abrir en módulo <ArrowSquareOut size={14} weight="bold" />
        </button>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-stone-400">{label}</div>
      <div className="text-sm text-stone-900 capitalize">{value}</div>
    </div>
  );
}
